/**
 * Return navigation is limited to local workspace routes, never arbitrary URLs: a same-origin path on the allowlist
 * (Today, "/", included: AIC-310 S8) with its own query and hash, or null. The path must match the allowlist exactly, so
 * a scheme, "//", a backslash, a control or space character, a dot segment or an encoded slash never gets through.
 */
export function localReturn(value: string | null): string | null {
  if (!value || value.length > 4096 || /[\\\u0000-\u0020]/.test(value)) return null;
  const path = value.split(/[?#]/, 1)[0]!;
  if (!/^\/(?:today|assignments(?:\/[a-zA-Z0-9_-]+(?:\/preview)?)?|review|insights(?:\/(?:funnel|quality|outcomes|digest|workflow|cost|pacing))?|dashboard|admin(?:\/analyst-preview|\/people|\/settings|\/cycle(?:\/reconciliation|\/imports)?)?|results\/[a-zA-Z0-9_-]+)?$/.test(path)) return null;
  return value;
}

/** The words on a link back to `target`, an allowlisted return (see `localReturn`): each return names its page. */
export function returnLabel(target: string): string {
  const path = target.split(/[?#]/, 1)[0]!;
  if (path === "/") return "Back to Analyst Workbench";
  if (path === "/today") return "Back to Today";
  if (path === "/review") return "Review queue";
  if (path.startsWith("/insights")) return "Back to Insights";
  if (path === "/dashboard") return "Back to Dashboard";
  if (path === "/admin/cycle/reconciliation") return "Back to Reconciliation";
  if (path === "/admin/cycle/imports") return "Back to Imports";
  if (path === "/admin/settings") return "Back to Settings";
  if (path.startsWith("/results/")) return "Back to result";
  if (path === "/admin/cycle") return "Back to Cycle readiness";
  if (path === "/admin/analyst-preview") return "Back to analyst preview";
  if (path === "/admin/people" || path === "/admin") return "Back to People";
  if (/^\/assignments\/[^/]+\/preview$/.test(path)) return "Back to preview";
  if (path.startsWith("/assignments/")) return "Back to assignment";
  return "Assignments";
}

export function withReturn(to: string, origin: string): string {
  const target = localReturn(origin);
  if (!target) return to;
  const [base, hash] = to.split("#");
  const [path, search = ""] = base!.split("?");
  const query = new URLSearchParams(search);
  query.set("return_to", target);
  return `${path}?${query}${hash ? `#${hash}` : ""}`;
}

export function routeHref(route: { path: string; query: URLSearchParams }): string {
  const search = route.query.toString();
  return `${route.path}${search ? `?${search}` : ""}`;
}

/** Update working context without scrolling or creating a history entry per key press. */
export function replaceContext(to: string): void {
  window.history.replaceState(window.history.state, "", to);
  window.dispatchEvent(new PopStateEvent("popstate"));
}
